import React, { useState } from "react";
import { useError } from "../hooks/useError";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { PhoneNumberUtil } from 'google-libphonenumber';
const phoneUtil = PhoneNumberUtil.getInstance();

import { 
    FormContainer, 
    HeaderContainer, 
    ButtonContainer, 
    Form, 
    Title, 
    Text 
} from "./styles";
import FontLoader, { GlobalStyles } from "../styles/global";

import { Input } from "../Input";
import { Button } from "../Button";
import { Alert } from "../Alert";
import { PhoneInput } from "../PhoneInput";

export interface MitreFormComponentProps {
  productId: string;
  apiUrl: string;
  apiToken: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  showHeader?: boolean;
  colorPrimary?: string;
  textColor?: string;
  backgroundColor?: string;
  innerPadding?: string;
}

const isPhoneValid = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    console.log('erro ao validar telefone = ' + error)
    return false;
  }
};

const schema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  email: yup.string().required("Email é obrigatório").email("Email inválido"),
  phone: yup.object().shape({
    phone: yup.string().required("Telefone é obrigatório!")
    .test(
      'is-valid-phone',
      'Número de telefone inválido!',
      function (value) { 
        const digitsOnly = value?.replace(/\D/g, '') || '';
        return digitsOnly.length >= 8 && isPhoneValid(value)
      }),
    inputValue: yup.string().required("Telefone é obrigatório!"),
    dialCode: yup.string().required("Código de país é obrigatório")
  }),
});

const MitreFormComponent = React.forwardRef<HTMLDivElement, MitreFormComponentProps>(({
  productId,
  apiUrl,
  apiToken,
  utm_source,
  utm_medium,
  utm_campaign,
  utm_term,
  showHeader =  true,
  colorPrimary = "#F6C76B",
  textColor = "#2F2F2F",
  backgroundColor = "#CECECE",
  innerPadding = "1rem",
}, ref) => {
  const [loading, setIsLoading] = useState(false);
  const { error, handleError, clearError } = useError();
  const [successMessage, setSuccessMessage] = useState('');
  const [formKey, setFormKey] = useState(0);

  const { control, register, handleSubmit, formState: { errors }, reset, clearErrors } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const resetForm = () => {
    reset({
      name: "",
      email: "",
      phone: { phone: "", inputValue: "", dialCode: "" },
    }, {
      keepErrors: false,
      keepDirty: false,
      keepTouched: false,
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepValues: false,
      keepDefaultValues: false,
    });
    clearErrors();
    setFormKey(k => k + 1); // Force remount
  };

  const sendMessage: SubmitHandler<{ name: string; email: string; phone: { phone: string; inputValue: string; dialCode: string; }; }> = async (data) => {    
    const { name, email, phone } = data;
  
    const phoneValue = phone.inputValue;
    const phoneDigitsOnly = phoneValue?.replace(/\D/g, '') || '';

    const message = "Gostaria de mais informações sobre o produto";

    try {
      setIsLoading(true);

      if (!productId || !utm_source || !utm_medium || !utm_campaign || !utm_term || !apiToken) {
        throw new Error("Parâmetros obrigatórios não informados");
      }

      const response = await fetch(`${apiUrl}/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${apiToken}`,
        },
        body: JSON.stringify({
          name,
          email,
          phone: phoneDigitsOnly,
          message,
          productId,
          utm_source,
          utm_medium,
          utm_campaign,
          utm_term,
        }),
      });

      if (!response.ok) {
        console.log('response = ' + JSON.stringify(response))
        throw new Error("Falha ao enviar a mensagem!");
      }

      setSuccessMessage("Mensagem enviada com sucesso!");
      resetForm();
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <>
        <FontLoader />
        <GlobalStyles />

        {error && (
          <Alert 
            type="error" 
            dismissible
            onDismiss={clearError}
            autoDismiss={5000}
          >
            {error!.message}
          </Alert>
        )}

        {successMessage && (
          <Alert 
            type="success"
            dismissible
            onDismiss={() => setSuccessMessage('')}
            autoDismiss={5000}
          >
            {successMessage}
          </Alert>
        )}

        <FormContainer $backgroundColor={backgroundColor} $innerPadding={innerPadding} ref={ref} >
          {showHeader && 
            <HeaderContainer>
              <Title $textColor={textColor}>Atendimento por mensagem</Title>

              <Text $textColor={textColor}>Informe seus dados e retornaremos a mensagem.</Text>
            </HeaderContainer>
          }

          <Form $textColor={textColor} onSubmit={handleSubmit(sendMessage)} noValidate>
            <Input
              id="name"
              label="Nome *"
              placeholder="Digite seu nome"
              {...register("name")}
              borderColor={colorPrimary}
              textColor={textColor}
              error={errors.name?.message}
              autoComplete="name"
              required
            />

            <Input
              id="email"
              label="Email *"
              type="email"
              placeholder="exemplo@email.com"
              {...register("email")}
              borderColor={colorPrimary}
              textColor={textColor}
              error={errors.email?.message}
              autoComplete="email"
              required
            />

            <Controller
              key={formKey}
              name="phone"
              control={control}
              defaultValue={{ phone: "", inputValue: "", dialCode: "" }}
              shouldUnregister={true}
              render={({ field }) => {
                const errorMsg =
                  errors.phone?.inputValue?.message ||
                  errors.phone?.phone?.message ||
                  errors.phone?.message;

                return (
                  <PhoneInput
                    id="phone"
                    label="Telefone *"
                    placeholder="(11) 00000-0000"
                    borderColor={colorPrimary}
                    textColor={textColor}
                    error={errorMsg}
                    required
                    value={field.value.phone}
                    onChange={field.onChange}
                    name={field.name}
                  />
                );
              }}
            />

            <h6>* Campos de preenchimento obrigatório.</h6>

            <ButtonContainer>
              <Button bgColor={colorPrimary} color={textColor} type="submit" isSubmitting={loading}>
                Enviar mensagem
              </Button>
            </ButtonContainer>

            <p>A Mitre Realty respeita a sua privacidade e utiliza os seus dados pessoais para contatá-lo por e-mail ou telefone aqui registrados. Para saber mais, acesse a nossa{ ' '}
            <a
              href="https://www.mitrerealty.com.br/politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Privacidade
            </a>. Ao clicar em {'"'}enviar{'"'}, você concorda em permitir que a Mitre Realty, armazene e processe os dados pessoais fornecidos por você para finalidade informada</p>

          </Form>
        </FormContainer>
      </>
  );
});

MitreFormComponent.displayName = "MitreFormComponent";

export default MitreFormComponent;
